import { useInvoiceInfo } from '../../app/Billing/Invoice/InvoiceInfo/InvoiceInfoProvider';
import useApi from '../../hook/useApi';
import cn from '../../utils/cn';
import { GetCurrencySymbol } from '../../utils/Currency';
import { GetFloatNumber } from '../../utils/NumberFormatter';
import { GetDate } from '../../utils/Show';
import { CrossIcon } from '../../utils/SVGIcon';
import { calcVat, formatVatCell } from '../../utils/VatCalculator';
import { useTemplate } from '../template/TemplateProvider';

function Tr({ item, serial, canEdit }) {
    const { setConfirm, setMessage, setTemplateLoading, openModal } = useTemplate();
    const { handleReload, invoiceInfo } = useInvoiceInfo();
    const { put } = useApi();

    const isUnpaid = invoiceInfo?.invoice?.amountPaid < 1;
    const isServiceItem = item?.productType === 'service';

    // VAT is editable on every line item while the invoice can still change;
    // removal stays limited to service items not yet linked to a subscription
    const invoiceEditable =
        canEdit &&
        isUnpaid &&
        invoiceInfo?.invoice?.authorizedBy <= 0 &&
        invoiceInfo?.invoice?.status !== 'canceled';
    const canRemoveItem = isServiceItem && item?.subscriptionServiceId === 0;

    const effectivePrice =
        item?.priceOffer > 0 && item?.priceOffer < item?.price ? item.priceOffer : item?.price || 0;

    const { netAmount, vatAmount, grossAmount } = calcVat(
        effectivePrice,
        item?.vatPercent,
        item?.vatType
    );
    const qty = item?.quantity || 0;
    const netLine = netAmount * qty;
    const vatLine = vatAmount * qty;
    const grossLine = grossAmount * qty;

    const handleRemove = (invoiceId, itemId, title) => {
        setConfirm({
            text: `Are you sure you want to remove the service item "${title}"?`,
            textAction: 'Yes, Remove',
            clickAction: () => {
                setTemplateLoading(true);
                put(`api/invoices/${invoiceId}/service-items/${itemId}/remove`)
                    .then(({ error, message }) => {
                        if (error) {
                            throw new Error(message);
                        }
                        setMessage({ type: 'success', text: message });
                        setConfirm(null);
                        handleReload();
                    })
                    .catch((ex) => {
                        setMessage({ type: 'error', text: ex?.message });
                    })
                    .finally(() => {
                        setTemplateLoading(false);
                    });
            }
        });
    };

    const handleModifyVat = () => {
        openModal('update-item-vat', {
            invoiceId: item?.invoiceId,
            itemId: item?.id,
            productTitle: item?.productTitle,
            price: effectivePrice,
            quantity: qty,
            vatPercent: item?.vatPercent || 0,
            vatType: item?.vatType || 'excluding',
            currency: item?.currency || invoiceInfo?.invoice?.currency
        });
    };

    return (
        <tr
            className={cn(
                `h-auto align-top group print:break-inside-avoid print:page-break-inside-avoid [&>td]:pt-5 [&>td>*]:text-xs2`,
                { 'placeholder-mode': item?.demo }
            )}
        >
            <td className="text-invoice-primary">
                <span>{serial}</span>
            </td>
            <td>
                <div className="flex justify-start items-start gap-2 mb-1">
                    <div>
                        <span className="text-invoice-primary">{item?.productTitle || '--'}</span>
                        <br />
                        <span className="text-invoice-secondary mt-px">
                            {item?.packageName || '--'}
                        </span>
                        <p className="text-invoice-secondary mt-px">
                            <span>{item?.property || ``}</span>
                        </p>
                        {item?.subscriptionStart > 0 && item?.productType === 'service' ? (
                            <p className="text-invoice-secondary">
                                <span>
                                    {GetDate(item?.subscriptionStart)} {' - '}
                                    {GetDate(item?.subscriptionEnd)}
                                </span>
                            </p>
                        ) : null}
                        {item?.domain && <span>&nbsp;#{item?.domain}</span>}
                    </div>
                </div>
            </td>
            <td className="text-invoice-primary text-center">
                <span>{item?.quantityString?.split('/').join('<br>')}</span>
            </td>
            <td className="text-center text-invoice-primary">
                <span>
                    {item?.priceUnitString?.toLowerCase() === 'free service' &&
                        GetCurrencySymbol(item?.currency)}
                    {item?.priceUnitString}
                </span>
            </td>
            {/* Net price for this line */}
            <td className="text-invoice-primary text-right">
                <span>{GetFloatNumber(netLine)}</span>
            </td>
            {/* VAT amount + rate */}
            <td className="text-invoice-primary text-center">
                <span>{formatVatCell(vatLine, item?.vatPercent)}</span>
            </td>
            {/* Gross total for this line */}
            <td className="text-invoice-primary text-right">
                <div className="invoice-item-total relative">
                    <span>{GetFloatNumber(grossLine)}</span>

                    {invoiceEditable ? (
                        <div className="absolute inline-flex items-center gap-1 top-5 right-1 print:hidden">
                            {/* Modify VAT */}
                            <button
                                type="button"
                                onClick={handleModifyVat}
                                className="inline-flex items-center justify-center w-6 h-6 bg-blue-100 hover:bg-blue-500 text-blue-600 hover:text-white rounded-full transition-colors"
                                aria-label="Modify VAT"
                                title="Modify VAT"
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 20 20"
                                    fill="currentColor"
                                    className="w-3 h-3"
                                >
                                    <path d="M2.695 14.763l-1.262 3.154a.5.5 0 00.65.65l3.155-1.262a4 4 0 001.343-.885L17.5 5.5a2.121 2.121 0 00-3-3L3.58 13.42a4 4 0 00-.885 1.343z" />
                                </svg>
                            </button>
                            {/* Remove */}
                            {canRemoveItem ? (
                                <button
                                    type="button"
                                    onClick={() =>
                                        handleRemove(
                                            item?.invoiceId,
                                            item?.id,
                                            item?.productDescription
                                        )
                                    }
                                    className="inline-flex items-center justify-center w-6 h-6 bg-red-200 hover:bg-error text-error hover:text-white rounded-full transition-colors"
                                    aria-label="Remove item"
                                >
                                    <CrossIcon />
                                </button>
                            ) : null}
                        </div>
                    ) : null}
                </div>
            </td>
        </tr>
    );
}

function InvoiceDataTable({ info }) {
    const items = info?.invoiceItems || [];
    const invoice = info?.invoice;

    // Footer totals
    let sumNet = 0;
    let sumVat = 0;
    let sumGross = 0;
    items.forEach((item) => {
        const ep =
            item.priceOffer > 0 && item.priceOffer < item.price ? item.priceOffer : item.price || 0;
        const { netAmount, vatAmount, grossAmount } = calcVat(ep, item.vatPercent, item.vatType);
        const q = item.quantity || 0;
        sumNet += netAmount * q;
        sumVat += vatAmount * q;
        sumGross += grossAmount * q;
    });

    const canEdit = true;

    return (
        <table className="w-full mb-5 table-fixed print:table-auto">
            <thead className="print:break-inside-avoid print:page-break-inside-avoid print:table-header-group">
                <tr className="*:text-xs2 *:font-bold *:text-invoice-primary">
                    <th className="w-[40px] text-left">Item</th>
                    <th className="text-left">Description</th>
                    <th className="w-[70px] text-center">Qty</th>
                    <th className="w-[90px] text-center">Price/Unit</th>
                    <th className="w-[80px] text-right">Price</th>
                    <th className="w-[110px] text-center">VAT</th>
                    <th className="w-[100px] text-right">Total ({invoice?.currency})</th>
                </tr>
            </thead>
            <tbody className="print:table-row-group">
                {items.map((item, i) => (
                    <Tr key={item.id} item={item} serial={i + 1} canEdit={canEdit} />
                ))}
            </tbody>
            {items.length > 0 && (
                <tfoot className="print:break-inside-avoid">
                    <tr className="*:text-xs2 *:font-semibold *:text-invoice-primary border-t border-invoice-primary/20">
                        <td colSpan={4} />
                        <td className="pt-3 text-right">{GetFloatNumber(sumNet)}</td>
                        <td className="pt-3 text-center">{GetFloatNumber(sumVat)}</td>
                        <td className="pt-3 text-right">{GetFloatNumber(sumGross)}</td>
                    </tr>
                </tfoot>
            )}
        </table>
    );
}

export default InvoiceDataTable;
